import React, { MouseEventHandler } from 'react';
import { DetailWidgetInteractionItem, StyleProps } from '../WidgetElement';
import { sendInteractGATrackEventData } from '../../../../util/gAUtil';

const generateTextStyle = (style: StyleProps, isHover: boolean, clickable: boolean) => {
	const {
		textAlignment,
		contentSpacing,
		headerLabel: {
			font: headerFont,
			fontSize: headerFontSize,
			fontWeight: headerFontWeight,
			lineHeight: headerLineHeight,
			color: headerColor,
			shadowColor: headerShadowColor,
		},
		textLabel: {
			font: paragraphFont,
			fontSize: paragraphFontSize,
			fontWeight: paragraphFontWeight,
			lineHeight: paragraphLineHeight,
			color: paragraphColor,
			shadowColor: paragraphShadowColor,
		},
		backgroundSettings: {
			backgroundColor,
			borderType,
			borderWidth,
			borderColor,
			hoverBackgroundColor,
			hoverBorderType,
			hoverBorderWidth,
			hoverBorderColor,
		},
		transformSettings: {
			paddingBottom,
			paddingLeft,
			paddingRight,
			paddingTop,
			radiusBottom,
			radiusLeft,
			radiusRight,
			radiusTop,
			hoverRadiusTop,
			hoverRadiusRight,
			hoverRadiusBottom,
			hoverRadiusLeft,
		},
	} = style;

	const backgroundStyle = {
		display: 'flex',
		flexDirection: 'column' as 'column',
		backgroundColor: !isHover ? backgroundColor : hoverBackgroundColor,
		border: !isHover ? `${borderWidth}px ${borderType} ${borderColor}` : `${hoverBorderWidth}px ${hoverBorderType} ${hoverBorderColor}`,
		paddingBottom: `${paddingBottom}px`,
		paddingLeft: `${paddingLeft}px`,
		paddingRight: `${paddingRight}px`,
		paddingTop: `${paddingTop}px`,
		borderTopRightRadius: !isHover ? `${radiusTop}px` : `${hoverRadiusTop}px`,
		borderBottomRightRadius: !isHover ? `${radiusRight}%` : `${hoverRadiusRight}px`,
		borderBottomLeftRadius: !isHover ? `${radiusBottom}%` : `${hoverRadiusBottom}px`,
		borderTopLeftRadius: !isHover ? `${radiusLeft}px` : `${hoverRadiusLeft}px`,
		width: '100%',
		height: '100%',
		cursor: clickable ? 'pointer' : 'null',
	};

	const headerStyle = {
		fontFamily: `${headerFont}, Arial, sans-serif`,
		fontSize: `${headerFontSize}px`,
		fontWeight: headerFontWeight,
		lineHeight: `${headerLineHeight}%`,
		textAlign: textAlignment as CanvasTextAlign,
		color: headerColor,
		textShadow: `2px 2px ${headerShadowColor}`,
		marginBottom: `${contentSpacing}px`,
	};

	const paragraphStyle = {
		fontFamily: `${paragraphFont}, Arial, sans-serif`,
		fontSize: `${paragraphFontSize}px`,
		fontWeight: paragraphFontWeight,
		lineHeight: `${paragraphLineHeight}%`,
		textAlign: textAlignment as CanvasTextAlign,
		color: paragraphColor,
		textShadow: `2px 2px ${paragraphShadowColor}`,
	};

	return {
		headerStyle,
		paragraphStyle,
		backgroundStyle,
	};
};

const Text = ({
	id,
	style,
	clickable,
	isHover,
	scenarioId,
	sceneId,
	widgetRef,
	data,
	onClick,
	handleMouseEnter,
	handleMouseLeave,
}: DetailWidgetInteractionItem) => {
	const {
		headerLabel: { text: header },
		textLabel: { text: paragraph },
		coordinate: { x, y },
		dimension: { w = 150, h = 100 },
		rotation,
	} = style;

	const { headerStyle, paragraphStyle, backgroundStyle } = generateTextStyle(style, isHover, clickable);

	const { type, name } = data;

	const centerPoint = {
		x: x + w - w / 2,
		y: y + h - h / 2,
	};

	const handleWidgetAction = (event: React.TouchEvent | React.MouseEvent) => {
		event.stopPropagation();
		sendInteractGATrackEventData(type, name, scenarioId, sceneId);
		onClick(event);
	};

	return (
		<g
			id={id}
			onTouchStart={handleWidgetAction}
			onClick={handleWidgetAction}
			onMouseEnter={handleMouseEnter as MouseEventHandler<SVGGElement>}
			onMouseLeave={handleMouseLeave as MouseEventHandler<SVGGElement>}
		>
			<foreignObject
				ref={widgetRef}
				x={x}
				y={y}
				width={w}
				height={h}
				style={{ transformOrigin: `${centerPoint.x}px ${centerPoint.y}px`, transform: `rotate(${rotation ?? 0}deg)` }}
			>
				<div style={backgroundStyle}>
					{headerStyle && <div style={headerStyle}>{header}</div>}
					<div style={paragraphStyle}>{paragraph}</div>
				</div>
			</foreignObject>
		</g>
	);
};

export default Text;
